import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import Header from "../components/common/Header";
import SideBar from "../components/common/SideBar";
import { GlobalIconNames } from "../utils/GlobalIconNames";
import ListOfReports from "../components/reports/ListOfReports";
import ReportForm from "../components/reports/ReportForm";
import ReportView from "../components/reports/ReportView";
import AuditTrail from "../components/reports/AuditTrail";
import { getReportDetails } from "../services/reportService";
import PermissionsGate from "../utils/permissionGate";
import { usePermissionGuardRedirect } from "../hooks/usePermissionGuard";

/**
 * Reports module page shell with list/detail tabs, permissions, and shared heading.
 * @returns {JSX.Element} Reports page layout.
 */
const Reports = () => {
  const history = useHistory();
  const location = useLocation();
  const { tab, id, subTab } = useParams();
  const [reportName, setReportName] = useState("");
  const prevReportIdRef = useRef(null);

  usePermissionGuardRedirect(["reportsview"]);

  useEffect(() => {
    if (!id) {
      setReportName("");
      prevReportIdRef.current = null;
      return undefined;
    }

    const idChanged = prevReportIdRef.current !== id;
    prevReportIdRef.current = id;

    if (idChanged && location.state?.reportName) {
      setReportName(location.state.reportName);
    } else if (idChanged) {
      setReportName("");
    }

    const cancelTokenSource = axios.CancelToken.source();

    const fetchReportName = async () => {
      try {
        const res = await getReportDetails({ id }, cancelTokenSource.token);
        if (res.data?.status) {
          setReportName(res.data.data?.name || "");
        }
      } catch (error) {
        if (
          !axios.isCancel(error) &&
          idChanged &&
          !location.state?.reportName
        ) {
          setReportName("");
        }
      }
    };

    fetchReportName();

    return () => {
      cancelTokenSource.cancel("Reports page unmounted or report id changed");
    };
  }, [id, location.state?.reportName]);

  const showListTabs = tab === "table" || (tab === "auditTrail" && !id);
  const showDetailTabs =
    (tab === "open" && !!id) || (tab === "auditTrail" && !!id);

  const headingSubtitle = (() => {
    if (tab === "table" || (tab === "auditTrail" && !id)) return "";
    if (tab === "create") return "Create New";
    return reportName;
  })();

  const showColon =
    headingSubtitle &&
    tab !== "table" &&
    !(tab === "auditTrail" && !id);

  return (
    <>
      <Header />
      <div className="content-wrapper design-token-page d-flex flex-wrap">
        <SideBar />
        <div className="sidebar-right">
          <div className="sub-menu-content-block">
            <div className="sub-menu-right-block">
              <div className="wrap-box-right">
                <div className="my-tickets-heading-blk flex-direction mb-2">
                  <div className="d-flex align-items-center">
                    <div className="name-w-head d-flex align-items-center">
                      <h4 className="text-left-align landing-heading heading_color_dynamic">
                        Reports
                        {headingSubtitle ? (
                          <>
                            {showColon ? ":" : " "}
                            <span className="ml-1">{headingSubtitle}</span>
                          </>
                        ) : null}
                      </h4>
                    </div>
                    <div className="text-md-right action2-blk">
                      <div className="ticket-view-btn d-flex align-items-center">
                        <div className="add-new-ticket-bx">
                          <button
                            onClick={() => history.goBack()}
                            title="Back"
                            className="btn btn-white-bordered"
                            type="button"
                          >
                            <i className="fal fa-angle-left"></i>Back
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tabs-wrap">
                  {(showListTabs || showDetailTabs) ? (
                    <ul className="nav nav-pills mb-4" id="tabs" role="tablist">
                      {showListTabs && (
                        <>
                          <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                            <li
                              className="nav-item"
                              onClick={() => history.push("/reports/table")}
                            >
                              <Link
                                className={`nav-link ${tab === "table" ? "active" : ""}`}
                                to="/reports/table"
                              >
                                {GlobalIconNames("Reports List", "fa-chart-pie-alt", "Reports List").html}
                              </Link>
                            </li>
                          </PermissionsGate>
                          <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                            <li
                              className="nav-item"
                              onClick={() => history.push("/reports/auditTrail")}
                            >
                              <Link
                                className={`nav-link ${tab === "auditTrail" && !id ? "active" : ""}`}
                                to="/reports/auditTrail"
                              >
                                {GlobalIconNames("auditTrail").html}
                              </Link>
                            </li>
                          </PermissionsGate>
                        </>
                      )}
                      {showDetailTabs && (
                        <>
                          <li
                            className="nav-item"
                            onClick={() => history.push(`/reports/open/${id}`)}
                          >
                            <Link
                              className={`nav-link ${tab === "open" ? "active" : ""}`}
                              to={`/reports/open/${id}`}
                            >
                              {GlobalIconNames("details").html}
                            </Link>
                          </li>
                          <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                            <li
                              className="nav-item"
                              onClick={() => history.push(`/reports/auditTrail/${id}`)}
                            >
                              <Link
                                className={`nav-link ${tab === "auditTrail" && !!id ? "active" : ""}`}
                                to={`/reports/auditTrail/${id}`}
                              >
                                {GlobalIconNames("auditTrail").html}
                              </Link>
                            </li>
                          </PermissionsGate>
                        </>
                      )}
                    </ul>
                  ) : null}
                  <div className="tab-content" id="pills-tabContent">
                    <div
                      className="tab-pane fade active show"
                      id="pills-tab11"
                      role="tabpanel"
                      aria-labelledby="pills-tab1"
                    >
                      {tab === "table" && (
                        <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                          <ListOfReports />
                        </PermissionsGate>
                      )}
                      {tab === "create" && (
                        <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                          <ReportForm />
                        </PermissionsGate>
                      )}
                      {tab === "open" && !subTab && (
                        <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                          <ReportForm />
                        </PermissionsGate>
                      )}
                      {tab === "preview" && (
                        <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                          <ReportView />
                        </PermissionsGate>
                      )}
                      {tab === "auditTrail" && (
                        <PermissionsGate scopes={["reportsview"]} RenderError={() => null}>
                          <AuditTrail />
                        </PermissionsGate>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reports;
